import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import './AdminPresetBrowser.css';

export function groupPresets(presets, groupBy) {
  const groups = new Map();
  for (const preset of presets) {
    let entries;
    if (groupBy === 'group') entries = preset.groups.map(group => [
      `${preset.locale}:${group.id}`, `${group.title} (${preset.locale})`,
    ]);
    else if (groupBy === 'folder') entries = [[preset.folder, preset.folder]];
    else if (groupBy === 'locale') entries = [[preset.locale, preset.locale]];
    else entries = [[preset.difficulty, preset.difficulty == null ? null : `난이도 ${preset.difficulty}`]];
    if (!entries.length) entries = [[null, null]];
    for (const [key, label] of entries) {
      const id = key == null ? 'unassigned' : String(key);
      if (!groups.has(id)) groups.set(id, { id, label: label || '미지정', items: [] });
      groups.get(id).items.push(preset);
    }
  }
  const compare = (a, b) => String(a).localeCompare(String(b), 'ko', { numeric: true });
  return [...groups.values()].sort((a, b) =>
    a.id === 'unassigned' ? 1 : b.id === 'unassigned' ? -1 : compare(a.label, b.label)
  ).map(group => ({ ...group, items: [...group.items].sort((a, b) =>
    a.sortOrder - b.sortOrder || compare(a.title, b.title) || compare(`${a.locale}:${a.id}`, `${b.locale}:${b.id}`)
  ) }));
}

export default function AdminPresetBrowser() {
  const [authorized, setAuthorized] = useState(() => Boolean(apiClient.getAdminToken()));
  const [presets, setPresets] = useState([]);
  const [groupBy, setGroupBy] = useState('difficulty');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  useEffect(() => {
    if (!authorized) return;
    let cancelled = false;
    setLoading(true); setError('');
    apiClient.getAdminPresetBrowser().then(result => {
      if (!cancelled) setPresets(result.presets);
    }).catch(err => {
      if (cancelled) return;
      if (err.status === 401) { apiClient.clearAdminToken(); setAuthorized(false); }
      else setError(err.message || 'Preset 목록을 불러오지 못했습니다. 새로고침으로 다시 시도해 주세요.');
    }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [authorized, reload]);
  const filtered = useMemo(() => {
    const search = query.trim().toLocaleLowerCase();
    return presets.filter(preset => [preset.title, preset.id, preset.folder, ...preset.groups.map(group => group.title)]
      .filter(Boolean).some(value => value.toLocaleLowerCase().includes(search)));
  }, [presets, query]);
  const groups = useMemo(() => groupPresets(filtered, groupBy), [filtered, groupBy]);
  const totalCards = useMemo(() => filtered.reduce((total, preset) => total + preset.cardCount, 0), [filtered]);
  if (!authorized) return <Navigate to="/admin" replace />;
  return <main className="admin-preset-browser">
    <header className="admin-preset-browser-toolbar">
      <Link to="/admin">관리자로 돌아가기</Link>
      <div className="admin-preset-browser-heading">
        <h1>Preset 둘러보기</h1>
        <button type="button" disabled={loading} onClick={() => setReload(value => value + 1)}>새로고침</button>
      </div>
      <div className="admin-preset-browser-filters">
        <label>그룹화 기준<select value={groupBy} onChange={event => setGroupBy(event.target.value)}>
          <option value="difficulty">난이도</option><option value="group">그룹</option>
          <option value="folder">폴더</option><option value="locale">언어</option>
        </select></label>
        <label>Preset 검색<input type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder="제목, 그룹, 폴더 또는 ID" /></label>
      </div>
    </header>
    {loading ? <p role="status">Preset 목록을 불러오는 중…</p> : error ? <p role="alert">{error}</p> : <>
      <p role="status">전체 {presets.length}개 중 {filtered.length}개 · {groups.length}개 그룹 · 총 카드 {totalCards.toLocaleString('ko-KR')}개</p>
      {!groups.length && <p>{presets.length ? '검색 결과가 없습니다. 검색어를 바꿔 보세요.' : '현재 게시된 프리셋이 없습니다.'}</p>}
      {groups.map(group => <section key={group.id} className="admin-preset-browser-group" aria-label={group.label}>
        <h2>{group.label} <span>{group.items.length}개</span></h2>
        <div className="admin-preset-browser-scroll" tabIndex={0} role="region" aria-label={`${group.label} 목록 (가로 스크롤 가능)`}>
          <table aria-label={group.label}>
            <thead><tr>{['제목', '그룹', '카드 수', 'Reading', 'Card', '난이도', 'Sort order'].map(title => <th scope="col" key={title}>{title}</th>)}</tr></thead>
            <tbody>{group.items.map(preset => <tr key={`${preset.locale}:${preset.id}`}>
              <th scope="row"><span>{preset.title}</span><small>{preset.locale} · {preset.id}</small></th>
              <td>{preset.groups.map(item => item.title).join(', ') || '미지정'}</td>
              <td className="is-number">{preset.cardCount}</td>
              <td>{preset.hasReading ? 'O' : 'X'}</td><td>{preset.hasCard ? 'O' : 'X'}</td>
              <td className="is-number">{preset.difficulty ?? '미지정'}</td><td className="is-number">{preset.sortOrder}</td>
            </tr>)}</tbody>
          </table>
        </div>
      </section>)}
    </>}
  </main>;
}
